/**
 * 部分放款提醒相关工具函数
 */
import type { ApplyChannelData } from '@/api/apply-product/types';
import type { quickLoanChannelType } from '@/api/quick-withdraw/types';
import ProductCommodity from '@/shared/commodity';

/**
 * 渠道信息的通用接口（用于部分放款提醒判断）
 */
interface ChannelInfo {
  productId?: string;
  loanPartTag?: boolean;
  capitalList?: { loanPartTag?: boolean }[];
}

/**
 * 判断是否应该展示部分放款提醒（核心逻辑）
 *
 * 业务逻辑：
 * 1. 非自营渠道：使用后端返回的 loanPartTag（渠道级别）
 * 2. 自营渠道：根据用户当前选择的提现模式（24+权益、36融担等）关联的资方列表匹配配置信息
 *    - 检查 capitalList 中所有资方的 loanPartTag
 *    - 若>=1个资方满足局部匹配（loanPartTag === true），即展示部分放款成功提醒
 *
 * @param channel 当前渠道信息
 * @returns 是否应该展示部分放款提醒
 */
function checkPartialLoanTip(channel: ChannelInfo | undefined): boolean {
  if (!channel) {
    return false;
  }

  const isAutarkyProduct = channel.productId === ProductCommodity.AUTARKY_B_COMMODITY;

  // 非自营渠道：使用后端返回的渠道级别的 loanPartTag
  if (!isAutarkyProduct) {
    return channel.loanPartTag === true;
  }

  // 自营渠道：检查 capitalList 中是否有至少一个资方的 loanPartTag 为 true
  const { capitalList = [] } = channel;

  if (!capitalList || capitalList.length === 0) {
    return false;
  }

  // 若>=1个资方满足局部匹配，即展示部分放款成功提醒
  return capitalList.some((capital) => capital.loanPartTag === true);
}

/**
 * 判断是否应该展示部分放款提醒（提现页场景）
 *
 * @param channel 当前渠道信息
 * @returns 是否应该展示部分放款提醒
 */
export function shouldShowPartialLoanTip(
  channel: ApplyChannelData | undefined,
): boolean {
  return checkPartialLoanTip(channel);
}

/**
 * 判断是否应该展示部分放款提醒（快速提现场景）
 *
 * @param quickLoanChannel 快速提现渠道信息
 * @returns 是否应该展示部分放款提醒
 */
export function shouldShowPartialLoanTipForQuick(
  quickLoanChannel: quickLoanChannelType | undefined,
): boolean {
  return checkPartialLoanTip(quickLoanChannel);
}
